<script setup lang="ts">
    import { computed, ComputedRef, ref } from 'vue';
    import _ from 'lodash';
    import ModalBase from '@/shared/components/ModalBase.vue';
    import TagFilterColumns from '@/shared/components/TagFilterColumns.vue';
    import { LibraryListItem } from '@/shared/helpers/api';

    const props = defineProps<{
        libraries: LibraryListItem[];
        selected: string[];
    }>();

    const emit = defineEmits<{
        confirm: [libraries: LibraryListItem[]];
        cancel: [];
    }>();

    const searchText = ref('');
    const selectedGroups = ref<string[]>([]);
    const selectedNames = ref<string[]>([...props.selected]);

    // Snapshot taken when the modal opens: these stay pinned and visible regardless
    // of filtering, and unchecking one means it should be removed from the package.
    const pinnedNames = new Set(props.selected);

    const libraryGroups: ComputedRef<string[]> = computed(() =>
        _.uniq(props.libraries.flatMap(library => library.tags)).sort());

    const sortedLibraries: ComputedRef<LibraryListItem[]> = computed(() => {
        const byName = _.sortBy(props.libraries, library => library.name.toLowerCase());
        return [
            ...byName.filter(library => pinnedNames.has(library.name)),
            ...byName.filter(library => !pinnedNames.has(library.name))
        ];
    });

    const filteredLibraries: ComputedRef<LibraryListItem[]> = computed(() => {
        const search = searchText.value.trim().toLowerCase();
        return sortedLibraries.value.filter(library => {
            if (pinnedNames.has(library.name))
                return true;

            const matchesGroup = selectedGroups.value.length === 0
                || library.tags.some(tag => selectedGroups.value.includes(tag));
            const matchesSearch = search === ''
                || library.name.toLowerCase().includes(search)
                || (library.description ?? '').toLowerCase().includes(search);
            return matchesGroup && matchesSearch;
        });
    });

    const isFiltered: ComputedRef<boolean> = computed(() =>
        filteredLibraries.value.length !== props.libraries.length);

    const selectAllLabel: ComputedRef<string> = computed(() =>
        isFiltered.value ? 'Select all shown' : 'Select all');

    const statusText: ComputedRef<string> = computed(() =>
        isFiltered.value
            ? `${selectedNames.value.length} selected \u00b7 showing ${filteredLibraries.value.length} of ${props.libraries.length}`
            : `${selectedNames.value.length} of ${props.libraries.length} selected`);

    const addedCount: ComputedRef<number> = computed(() =>
        selectedNames.value.filter(name => !pinnedNames.has(name)).length);

    const removedCount: ComputedRef<number> = computed(() =>
        [...pinnedNames].filter(name => !selectedNames.value.includes(name)).length);

    const hasChanges: ComputedRef<boolean> = computed(() =>
        addedCount.value > 0 || removedCount.value > 0);

    function pluralLibraries(count: number): string {
        return count === 1 ? '1 library' : `${count} libraries`;
    }

    const confirmButtonLabel: ComputedRef<string> = computed(() => {
        if (addedCount.value > 0 && removedCount.value === 0)
            return `Add ${pluralLibraries(addedCount.value)}`;
        if (removedCount.value > 0 && addedCount.value === 0)
            return `Remove ${pluralLibraries(removedCount.value)}`;
        return 'Update selection';
    });

    function selectAllFiltered(): void {
        selectedNames.value = _.union(selectedNames.value, filteredLibraries.value.map(library => library.name));
    }

    function clearSelection(): void {
        selectedNames.value = [];
    }

    function clearGroupFilter(): void {
        selectedGroups.value = [];
    }

    function isPinned(library: LibraryListItem): boolean {
        return pinnedNames.has(library.name);
    }

    function onSubmit(): void {
        if (!hasChanges.value)
            return;

        emit('confirm', sortedLibraries.value.filter(library => selectedNames.value.includes(library.name)));
    }
</script>

<template>
    <modal-base
        max-width="800px"
        width="52rem"
        max-height="calc(100vh - 4em)"
        @on-cancel="emit('cancel')">
        <template #modal-header>
            Select libraries
        </template>
        <template #modal-body>
            <b-field grouped>
                <b-input
                    v-model="searchText"
                    placeholder="Search libraries.."
                    type="search"
                    icon="magnify"
                    expanded />
                <p class="control">
                    <b-dropdown
                        aria-role="list"
                        position="is-bottom-left">
                        <template #trigger>
                            <b-button
                                type="is-info"
                                icon-right="menu-down"
                                :disabled="libraryGroups.length === 0">
                                Library groups ({{ selectedGroups.length }})
                            </b-button>
                        </template>
                        <b-field
                            class="pt-3 px-3 pb-0"
                            position="is-centered">
                            <p class="control">
                                <b-button
                                    size="is-small"
                                    :disabled="selectedGroups.length === 0"
                                    @click="clearGroupFilter">
                                    Clear selected
                                </b-button>
                            </p>
                        </b-field>
                        <tag-filter-columns
                            class="tag-filter-columns"
                            :all-tags="libraryGroups"
                            :active-tags="selectedGroups"
                            @update:tags="selectedGroups = $event" />
                    </b-dropdown>
                </p>
            </b-field>

            <b-field grouped>
                <p class="control">
                    <b-button
                        size="is-small"
                        :disabled="filteredLibraries.length === 0"
                        @click="selectAllFiltered">
                        {{ selectAllLabel }}
                    </b-button>
                </p>
                <p class="control">
                    <b-button
                        size="is-small"
                        :disabled="selectedNames.length === 0"
                        @click="clearSelection">
                        Clear selection
                    </b-button>
                </p>
                <p class="control is-flex is-align-items-center has-text-grey">
                    {{ statusText }}
                </p>
            </b-field>

            <div class="shipping-library-selector-list">
                <p
                    v-if="filteredLibraries.length === 0"
                    class="has-text-grey p-3">
                    No libraries match the current filter.
                </p>
                <div
                    v-for="library in filteredLibraries"
                    :key="library.name"
                    class="shipping-library-selector-item"
                    :class="{ 'is-pinned': isPinned(library) }">
                    <b-checkbox
                        v-model="selectedNames"
                        :native-value="library.name"
                        :title="library.description">
                        <span class="shipping-library-selector-name has-text-weight-bold">{{ library.name }}</span>
                        <span
                            v-if="library.description"
                            class="shipping-library-selector-description has-text-grey">{{ library.description }}</span>
                    </b-checkbox>
                </div>
            </div>
        </template>
        <template #modal-footer>
            <div class="buttons">
                <b-button
                    label="Cancel"
                    @click="emit('cancel')" />
                <b-button
                    :label="confirmButtonLabel"
                    type="is-primary"
                    :disabled="!hasChanges"
                    @click="onSubmit" />
            </div>
        </template>
    </modal-base>
</template>

<style scoped>
/*
    The modal is capped by max-height, so the body becomes a flex column and the
    library list takes whatever space is left. Search controls, the filter row and
    the footer stay visible, and only the list scrolls.
*/
:deep(.modal-card-body) {
    display: flex;
    flex-direction: column;
    min-height: 0;
    /* Let the library group dropdown escape the body instead of being clipped */
    overflow: visible;
}

:deep(.modal-card-body > div) {
    display: flex;
    flex-direction: column;
    min-height: 0;
    flex: 1 1 auto;
}

:deep(.modal-card) {
    overflow: visible;
}

.shipping-library-selector-list {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    border: solid 1px var(--bulma-border);
}

.shipping-library-selector-item {
    padding: 0.25em 0.75em;
}

.shipping-library-selector-item:nth-child(even) {
    background-color: var(--bulma-white-bis);
}

/* Separate the already-selected libraries pinned at the top from the rest */
.shipping-library-selector-item.is-pinned + .shipping-library-selector-item:not(.is-pinned) {
    border-top: solid 2px var(--bulma-grey-light);
}

/* Keep every row on a single line so the list stays evenly spaced */
.shipping-library-selector-item :deep(.b-checkbox) {
    display: flex;
    width: 100%;
    align-items: center;
}

.shipping-library-selector-item :deep(.control-label) {
    display: flex;
    align-items: baseline;
    gap: 0.4em;
    flex: 1 1 auto;
    min-width: 0;
}

.shipping-library-selector-name {
    flex: 0 0 auto;
}

/* Only the truncation needs custom CSS; colour comes from the has-text-grey helper */
.shipping-library-selector-description {
    flex: 0 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/*
    Sized against the viewport so the group list always fits on screen. The fixed
    pixel sizes previously used could exceed the viewport at higher zoom levels,
    putting groups out of reach. TagFilterColumns wraps into columns to fill the
    height, and scrolls horizontally if they exceed the width.
*/
.tag-filter-columns {
    height: min(420px, 50vh);
    width: min(400px, 70vw);
    margin: 5px;
}
</style>
